import type { ITransport } from './types';

/**
 * Configuration options for SSETransport.
 */
export interface ISSETransportOptions {
  /**
   * Default event type to use when the SSE message doesn't include a type field.
   * Useful for endpoints that send payload-only messages.
   */
  defaultEventType?: string;

  /**
   * Maximum number of reconnection attempts before giving up.
   * @default 5
   */
  maxReconnectAttempts?: number;

  /**
   * Base delay in milliseconds for exponential backoff.
   * @default 1000
   */
  baseRetryDelay?: number;
}

/**
 * SSE (Server-Sent Events) transport for realtime event delivery.
 *
 * Uses the browser's EventSource API to receive events from the backend.
 * Automatically reconnects with exponential backoff on disconnect.
 *
 * @example
 * ```typescript
 * const transport = new SSETransport(
 *   () => `${BLACKBOX_URL}/stream/${accountId}/donations/`
 * );
 *
 * transport.onEvent((type, payload) => {
 *   console.log('Received event:', type, payload);
 * });
 *
 * await transport.connect();
 * ```
 */
class SSETransport implements ITransport {
  private eventSource: EventSource | null = null;
  private eventHandler: ((type: string, payload: unknown) => void) | null = null;
  private reconnectAttempts = 0;
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly defaultEventType: string | undefined;
  private readonly maxReconnectAttempts: number;
  private readonly baseRetryDelay: number;
  private intentionalDisconnect = false;
  private hasConnectedOnce = false;

  /**
   * Creates an SSE transport instance.
   *
   * @param getUrl - Function that returns the SSE endpoint URL
   * @param options - Configuration options
   */
  constructor(
    private readonly getUrl: () => string,
    options: ISSETransportOptions = {},
  ) {
    this.defaultEventType = options.defaultEventType;
    this.maxReconnectAttempts = options.maxReconnectAttempts ?? 5;
    this.baseRetryDelay = options.baseRetryDelay ?? 1000;
  }

  /**
   * Establish connection to the SSE endpoint.
   * @returns Promise that resolves when connected
   */
  public async connect(): Promise<void> {
    this.intentionalDisconnect = false;
    this.reconnectAttempts = 0;
    this.hasConnectedOnce = false;

    return this.establishConnection();
  }

  /**
   * Internal method to establish the EventSource connection.
   * Separated from connect() to allow reconnection without resetting state.
   */
  private establishConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Clean up any existing connection before creating a new one
        if (this.eventSource) {
          this.eventSource.close();
          this.eventSource = null;
        }

        const url = this.getUrl();
        this.eventSource = new EventSource(url, { withCredentials: true });

        this.eventSource.onopen = (): void => {
          this.reconnectAttempts = 0;
          this.hasConnectedOnce = true;
          resolve();
        };

        this.eventSource.onmessage = (event: MessageEvent): void => {
          this.handleMessage(event);
        };

        this.eventSource.onerror = (): void => {
          /*
           * If we haven't ever connected successfully, reject the promise.
           * This handles initial connection failures.
           */
          if (!this.hasConnectedOnce) {
            reject(new Error('Failed to connect to SSE endpoint'));
            return;
          }

          if (this.eventSource?.readyState === EventSource.CLOSED) {
            if (!this.intentionalDisconnect) {
              this.attemptReconnect();
            }
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Close the connection to the SSE endpoint.
   */
  public disconnect(): void {
    this.intentionalDisconnect = true;

    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  /**
   * Register a handler for incoming events.
   * @param handler - Callback invoked with event type and payload
   */
  public onEvent(handler: (type: string, payload: unknown) => void): void {
    this.eventHandler = handler;
  }

  /**
   * Whether the transport is currently connected.
   */
  public get isConnected(): boolean {
    return this.eventSource?.readyState === EventSource.OPEN;
  }

  /**
   * Current number of reconnection attempts.
   * Exposed for testing and debugging.
   */
  public get currentReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  /**
   * Handles an SSE message event.
   * Parses the JSON data and dispatches to the event handler.
   */
  private handleMessage(event: MessageEvent): void {
    if (!this.eventHandler) {
      return;
    }

    try {
      const data = JSON.parse(event.data as string) as {
        type?: string;
        payload?: unknown;
      };

      /*
       * Handle multiple envelope formats:
       * 1. { type, payload } - direct event format with explicit type
       * 2. { payload: { ... } } - payload-only format (uses defaultEventType)
       * 3. { ... } - raw data format (uses defaultEventType, data becomes payload)
       */
      const eventType = data.type ?? this.defaultEventType;
      const eventPayload = data.payload ?? data;

      if (eventType) {
        this.eventHandler(eventType, eventPayload);
      }
    } catch {
      /*
       * Silently ignore malformed JSON.
       * This handles keepalive comments and other non-event data.
       */
    }
  }

  /**
   * Attempts to reconnect with exponential backoff.
   */
  private attemptReconnect(): void {
    if (this.intentionalDisconnect) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    this.reconnectAttempts += 1;
    /* Exponential backoff: 1s, 2s, 4s, 8s, 16s */
    const delay = this.baseRetryDelay * Math.pow(2, this.reconnectAttempts - 1);

    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectTimeoutId = null;
      if (!this.intentionalDisconnect) {
        this.establishConnection().catch(() => {
          /* Reconnection will be attempted again via error handler */
        });
      }
    }, delay);
  }
}

export default SSETransport;
